import { Fragment, MouseEvent, useContext, useEffect, useState } from "react";
import { CSSObject, styled, Theme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Logout from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import { useHistory, useLocation } from "react-router";
import GlobalSearch from "components/GlobalSearch/GlobalSearch";
import useStateWithLocalStorage from "hooks/UseStateWithLocalStorage";
import AppStateContext from "../contexts/AppStateContext";
import { infoColor, whiteColor } from "../assets/colors";
import { ReactComponent as LogoIcon } from "../assets/svg/logoBlack.svg";
import SidebarProfile from "./SidebarProfile";
import {
  findGroupForPath,
  isNavGroup,
  isPathActive,
  NAV_ITEMS,
} from "./navigation";
import "./DashboardLayout.css";

export const DRAWER_WIDTH = 240;

const SIDEBAR_BG = "#000";
const ACTIVE_BG = "var(--header)";

//  THE BELOW STYLING IS FROM MUI AND REQUIRED FOR THE LOOKS OF THE DRAWER! - https://mui.com/components/drawers/
const openedMixin = (theme: Theme): CSSObject => ({
  background: infoColor[0],
  color: whiteColor,
  width: DRAWER_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  background: infoColor[0],
  color: whiteColor,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  marginBottom: "-8px",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open: boolean }>(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));
// END OF MUI DRAWER STYLING!

// Menu rows are anchors so they can be opened in a new tab, which means the
// global `a` styling has to be neutralised here.
const linkSx = {
  color: "inherit",
  textDecoration: "none",
  "&:hover": { fontWeight: "inherit", backgroundColor: ACTIVE_BG },
} as const;

const topLevelSx = {
  boxShadow: "0 1px 3px #075E5B",
  "&:hover": { backgroundColor: ACTIVE_BG },
} as const;

const subPageSx = {
  boxShadow: "0 0px 2px #075E5B",
} as const;

/** True when the click should be left to the browser (new tab, new window). */
const isModifiedClick = (event: MouseEvent) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

const Sidebar = () => {
  const { setShowLogoutModal } = useContext(AppStateContext);
  const history = useHistory();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [expandedGroup, setExpandedGroup] = useStateWithLocalStorage<
    string | null
  >(() => findGroupForPath(location.pathname), "openItem");

  // Keep the group holding the current route open, also for deep links and
  // navigation that does not come from the menu (global search, redirects).
  useEffect(() => {
    const group = findGroupForPath(location.pathname);
    if (group) setExpandedGroup(group);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (name: string) => {
    // Sub pages are not rendered in mini mode, so expanding implies opening.
    setDrawerOpen(true);
    setExpandedGroup((current) => (current === name ? null : name));
  };

  const navigate = (
    event: MouseEvent,
    path: string,
    collapseGroups = false,
  ) => {
    if (isModifiedClick(event)) return;
    event.preventDefault();
    if (collapseGroups) setExpandedGroup(null);
    history.push(path);
  };

  return (
    <Drawer variant="permanent" open={drawerOpen}>
      <DrawerHeader
        style={{
          background: SIDEBAR_BG,
          justifyContent: drawerOpen ? "flex-end" : "center",
        }}
      >
        {drawerOpen ? (
          <>
            <span style={{ position: "relative", right: "15%" }}>
              <LogoIcon />
            </span>
            <IconButton aria-label="close menu" onClick={() => setDrawerOpen(false)}>
              <ChevronLeftIcon className={"listIcon"} />
            </IconButton>
          </>
        ) : (
          <IconButton
            color="inherit"
            aria-label="open menu"
            onClick={() => setDrawerOpen(true)}
            edge="start"
          >
            <MenuIcon />
          </IconButton>
        )}
      </DrawerHeader>

      <SidebarProfile />

      <div style={{ background: SIDEBAR_BG }}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            paddingTop: "0.5rem",
          }}
        >
          <GlobalSearch />
        </div>
      </div>

      <List component="nav" style={{ background: SIDEBAR_BG }}>
        {NAV_ITEMS.map((item) => {
          if (!isNavGroup(item)) {
            const active = isPathActive(location.pathname, item.path);
            return (
              <Tooltip
                key={item.name}
                title={drawerOpen ? "" : item.name}
                placement="right"
              >
                <ListItemButton
                  component="a"
                  href={item.path}
                  onClick={(event: MouseEvent) =>
                    navigate(event, item.path, true)
                  }
                  aria-current={active ? "page" : undefined}
                  sx={{
                    ...linkSx,
                    ...topLevelSx,
                    backgroundColor: active ? ACTIVE_BG : undefined,
                  }}
                >
                  <ListItemIcon style={{ minWidth: "36px" }}>
                    {item.icon}
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary={item.name} />}
                </ListItemButton>
              </Tooltip>
            );
          }

          const expanded = expandedGroup === item.name;
          return (
            <Fragment key={item.name}>
              <Tooltip title={drawerOpen ? "" : item.name} placement="right">
                <ListItemButton
                  onClick={() => toggleGroup(item.name)}
                  aria-expanded={expanded}
                  sx={topLevelSx}
                >
                  <ListItemIcon style={{ minWidth: "36px" }}>
                    {item.icon}
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary={item.name} />}
                  {expanded ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </Tooltip>
              <Collapse in={drawerOpen && expanded} timeout="auto">
                <List component="div" disablePadding>
                  {item.pages.map((page) => {
                    const active = isPathActive(location.pathname, page.path);
                    const Badge = page.badge;
                    return (
                      <ListItemButton
                        key={page.name}
                        component="a"
                        href={page.path}
                        onClick={(event: MouseEvent) =>
                          navigate(event, page.path)
                        }
                        aria-current={active ? "page" : undefined}
                        sx={{
                          ...linkSx,
                          ...subPageSx,
                          backgroundColor: active ? ACTIVE_BG : undefined,
                        }}
                      >
                        <ListItemText
                          style={{ paddingLeft: "1.5rem" }}
                          primary={page.name}
                        />
                        {Badge && <Badge active={active} />}
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </Fragment>
          );
        })}
      </List>

      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          justifyContent: "flex-end",
          background: SIDEBAR_BG,
        }}
      >
        <Button
          variant="contained"
          style={{
            background:
              "linear-gradient(0deg, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)), #E02C4F",
            borderRadius: "0",
          }}
          onClick={() => setShowLogoutModal(true)}
          fullWidth
        >
          <ListItemIcon style={{ minWidth: "36px", color: "#fff" }}>
            <Logout />
          </ListItemIcon>
          {drawerOpen && <span>SIGN OUT</span>}
        </Button>
      </div>
    </Drawer>
  );
};

export default Sidebar;
